import { useEffect, useCallback } from "react";
import { err } from ".";

const TRUSTED_FORM_CERT_FIELD = "xxTrustedFormCertUrl";
const TRUSTED_FORM_PING_FIELD = "xxTrustedFormPingUrl";
const TRUSTED_FORM_ORIGIN = "https://api.trustedform.com";

function isValidTrustedFormUrl(url: string): boolean {
    try {
        const parsed = new URL(url);
        return parsed.origin === TRUSTED_FORM_ORIGIN;
    } catch {
        return false;
    }
}

export function useTrustedForm(scriptUrl: string | undefined): () => Record<string, string> | undefined {
    useEffect(() => {
        if (!scriptUrl) {
            return;
        }

        if (!isValidTrustedFormUrl(scriptUrl)) {
            err("[TrustedForm] Rejected script URL from unexpected origin");
            return;
        }

        const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);
        if (existingScript) {
            return;
        }

        const script = document.createElement("script");
        script.async = true;
        script.src = scriptUrl;

        script.onerror = () => {
            err("[TrustedForm] Failed to load script");
        };

        // Intentionally no cleanup on unmount — TrustedForm must remain active for the page session
        document.head.appendChild(script);
    }, [scriptUrl]);

    const getTrustedFormData = useCallback((): Record<string, string> | undefined => {
        if (!scriptUrl) {
            return undefined;
        }

        const data: Record<string, string> = {};

        const certInput = document.querySelector<HTMLInputElement>(
            `input[name="${TRUSTED_FORM_CERT_FIELD}"], input[id="${TRUSTED_FORM_CERT_FIELD}"]`
        );
        const pingInput = document.querySelector<HTMLInputElement>(
            `input[name="${TRUSTED_FORM_PING_FIELD}"], input[id="${TRUSTED_FORM_PING_FIELD}"]`
        );

        if (certInput?.value) {
            data[TRUSTED_FORM_CERT_FIELD] = certInput.value;
        }
        if (pingInput?.value) {
            data[TRUSTED_FORM_PING_FIELD] = pingInput.value;
        }

        if (Object.keys(data).length === 0) {
            err("[TrustedForm] No certificate data found in DOM");
            return undefined;
        }

        return data;
    }, [scriptUrl]);

    return getTrustedFormData;
}
